/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useRef, useMemo, useEffect } from '@web-stories-wp/react';
import { useUnits } from '@web-stories-wp/units';

/**
 * Internal dependencies
 */
import getCaretCharacterOffsetWithin from '../../utils/getCaretCharacterOffsetWithin';
import { useStory, useCanvas, useFont } from '../../app';
import {
  elementFillContent,
  elementWithFont,
  elementWithTextParagraphStyle,
} from '../shared';
import StoryPropTypes from '../../types';
import areEventsDragging from '../../utils/areEventsDragging';
import { getCaretCompatibleListMarkup } from '../../utils/compactListMarkup';
import { generateParagraphTextStyle } from './util';
import { calcFontMetrics } from './utils';
import {
  collectTextFontUsage,
  hasMixedTypographyMarkers,
  materializeTextContent,
} from './utils/mixedTypography';
import { elementWithTextList } from './listStyles';
import { hasSemanticList } from './utils/listStyle';
import useSanitizedTextElement from './utils/useSanitizedTextElement';
import useTextLayoutElement from './utils/useTextLayoutElement';

const Element = styled.div`
  ${elementFillContent}
  ${elementWithFont}
  ${elementWithTextParagraphStyle}
  ${elementWithTextList}

  opacity: 0;
  user-select: none;
`;

const InteractionBounds = styled.div`
  ${elementFillContent}

  /* Keep the transparent caret clone from extending the canvas hit area. */
  overflow: hidden;
`;

function TextFrame({ element: untrustedElement, wrapperRef }) {
  const element = useSanitizedTextElement(untrustedElement);
  const layoutElement = useTextLayoutElement(element);
  const { id, content, isLocked, ...rest } = element;
  const hasList = hasSemanticList(content);
  const hasMixedTypography = hasMixedTypographyMarkers(content);
  const { dataToEditorX, dataToEditorY } = useUnits((state) => ({
    dataToEditorX: state.actions.dataToEditorX,
    dataToEditorY: state.actions.dataToEditorY,
  }));

  const { loadedFonts = [] } = useFont(({ state: { loadedFonts } }) => ({
    loadedFonts,
  }));

  const fontUsage = useMemo(
    () => collectTextFontUsage(layoutElement),
    [layoutElement]
  );
  const usedRenderFamilies = useMemo(
    () => new Set(fontUsage.map(({ renderFamily }) => renderFamily)),
    [fontUsage]
  );
  const fontLoaded = useMemo(
    () =>
      loadedFonts.filter((loadedFont) =>
        usedRenderFamilies.has(
          loadedFont.renderFamily || loadedFont.font.family
        )
      ),
    [loadedFonts, usedRenderFamilies]
  );

  const { marginOffsetTop } = useMemo(
    () =>
      calcFontMetrics({
        element: {
          content: element.content,
          fontReferences: element.fontReferences,
          color: layoutElement.color,
          font: element.font,
          fontSize: element.fontSize,
          fontStyle: element.fontStyle,
          fontWeight: element.fontWeight,
          lineHeight: element.lineHeight,
          verticalTrim: element.verticalTrim,
          width: element.width,
          backgroundTextMode: element.backgroundTextMode,
          padding: element.padding,
          listStyle: element.listStyle,
        },
        dataToEditorX,
        dataToEditorY,
      }),
    [
      element.content,
      element.fontReferences,
      layoutElement.color,
      element.font,
      element.fontSize,
      element.fontStyle,
      element.fontWeight,
      element.lineHeight,
      element.verticalTrim,
      element.width,
      element.backgroundTextMode,
      element.padding,
      element.listStyle,
      dataToEditorX,
      dataToEditorY,
      fontLoaded,
    ]
  );

  const props = generateParagraphTextStyle(
    rest,
    dataToEditorX,
    dataToEditorY,
    undefined,
    marginOffsetTop,
    element
  );
  const displayContent = useMemo(
    () =>
      materializeTextContent(element, {
        fontSizeToCss: (size) => `${dataToEditorY(size)}px`,
      }),
    [dataToEditorY, element]
  );
  const renderedContent = useMemo(
    () =>
      getCaretCompatibleListMarkup(displayContent, {
        defaultFontSizeCss: `${dataToEditorY(element.fontSize)}px`,
        defaultLineHeight: element.lineHeight,
        includeListMarkerMetrics: hasMixedTypography,
      }),
    [
      dataToEditorY,
      displayContent,
      element.fontSize,
      element.lineHeight,
      hasMixedTypography,
    ]
  );
  const { selectedElementIds } = useStory((state) => ({
    selectedElementIds: state.state.selectedElementIds,
  }));

  const { setEditingElementWithState } = useCanvas((state) => ({
    setEditingElementWithState: state.actions.setEditingElementWithState,
  }));
  const isElementSelected = selectedElementIds.includes(id);
  const isElementOnlySelection =
    isElementSelected && selectedElementIds.length === 1;

  const interactionBoundsRef = useRef();
  const elementRef = useRef();

  useEffect(() => {
    if (!isElementOnlySelection) {
      return undefined;
    }

    const wrapper = wrapperRef.current;
    const interactionBounds = interactionBoundsRef.current;

    let clickTime = 0;
    let clickCoordinates = null;

    const handleKeyDown = (evt) => {
      if (evt.metaKey || evt.altKey || evt.ctrlKey || isLocked) {
        // Some modifier (except shift) was pressed. Ignore and bubble
        return;
      }

      if (evt.key === 'Enter' || /^\w$/.test(evt.key)) {
        // TODO: in above check all printable characters across alphabets, no just a-z0-9 as \w regex is
        // Enter on editing mode. When inserting content, first letter will be correctly inserted from keyup
        setEditingElementWithState(id, { selectAll: true });
        evt.stopPropagation();
        // Make sure no actual Enter is pressed
        if (evt.key === 'Enter') {
          evt.preventDefault();
        }
      }
    };

    const handleMouseDown = (evt) => {
      clickTime = evt.timeStamp;
      clickCoordinates = {
        clientX: evt.clientX,
        clientY: evt.clientY,
      };
    };

    const handleMouseUp = (evt) => {
      if (
        isLocked ||
        !clickCoordinates ||
        areEventsDragging(
          {
            timeStamp: clickTime,
            ...clickCoordinates,
          },
          evt
        )
      ) {
        // Abort early as this is part of a user dragging
        return;
      }

      // Enter editing mode and place cursor at current selection offset
      evt.stopPropagation();
      setEditingElementWithState(id, {
        offset: getCaretCharacterOffsetWithin(
          elementRef.current,
          evt.clientX,
          evt.clientY
        ),
      });
    };

    wrapper.addEventListener('keydown', handleKeyDown);
    interactionBounds.addEventListener('mousedown', handleMouseDown);
    interactionBounds.addEventListener('mouseup', handleMouseUp);
    return () => {
      wrapper.removeEventListener('keydown', handleKeyDown);
      interactionBounds.removeEventListener('mousedown', handleMouseDown);
      interactionBounds.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    id,
    wrapperRef,
    isElementOnlySelection,
    isLocked,
    setEditingElementWithState,
  ]);

  // data-fix-caret is for allowing caretRangeFromPoint to work in Safari.
  // See https://github.com/google/web-stories-wp/issues/7745.
  return (
    <InteractionBounds
      ref={interactionBoundsRef}
      data-testid="textFrameInteractionBounds"
    >
      <Element
        as={hasList ? 'div' : 'p'}
        ref={elementRef}
        data-testid="textFrame"
        data-fix-caret
        className="syncMargin"
        dangerouslySetInnerHTML={{ __html: renderedContent }}
        element={element}
        $hasList={hasList}
        $matchSvgListMetrics={hasMixedTypography}
        dataToEditorX={dataToEditorX}
        dataToEditorY={dataToEditorY}
        marginOffsetTop={marginOffsetTop}
        {...props}
      />
    </InteractionBounds>
  );
}

TextFrame.propTypes = {
  element: StoryPropTypes.elements.text.isRequired,
  wrapperRef: PropTypes.object.isRequired,
};

export default TextFrame;
